import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import semver from 'semver';
import { FE_BUILD_VERSION } from '_constants';

// Single-fire latch shared across every axios instance that calls this. One
// toast per page life is enough — re-firing on every API response would spam
// the user during normal navigation after a deploy.
let mismatchNotified = false;

// Latest BE version observed on any response. Display-only — surfaced via
// useBeVersion in UserInformationModal. SW-served offline responses are
// skipped so the value reflects the actual remote BE, not synthesized fakes.
let currentBeVersion = null;
const beVersionListeners = new Set();

const SW_HEADER_VALUE = 'This response generated by Service Worker';
const FE_VERSION_HEADER = 'x-fe-version';
const BE_VERSION_HEADER = 'x-be-version';

export const VERSION_MISMATCH_EVENT = 'fe-version-mismatch';

const captureBeVersion = (headers) => {
  const value = headers[BE_VERSION_HEADER];
  if (!value || value === currentBeVersion) return;
  currentBeVersion = value;
  beVersionListeners.forEach((listener) => listener(value));
};

export const useBeVersion = () => {
  const [version, setVersion] = useState(currentBeVersion);
  useEffect(() => {
    beVersionListeners.add(setVersion);
    return () => {
      beVersionListeners.delete(setVersion);
    };
  }, []);
  return version;
};

const checkVersionMismatch = (response) => {
  const headers = response?.headers;
  if (!headers) return;

  // SW-served offline responses are synthesized from IDB and carry no real
  // version headers — skip both BE-version capture and FE-mismatch check.
  // Header set in src/service-worker.js getProfileResponse / getRetailer / etc.
  if (headers.sw === SW_HEADER_VALUE) return;

  captureBeVersion(headers);

  if (mismatchNotified) return;

  // Local dev runs with manually-bumped FE_BUILD_VERSION; firing the toast
  // there would be pure noise. Production-only check.
  // if (process.env.NODE_ENV !== 'production') return;

  const serverFeVersion = headers[FE_VERSION_HEADER];
  if (!serverFeVersion) return;

  // Only nag when FE is genuinely behind what BE expects. If FE is at or
  // ahead of the expected version (mid-deploy, canary, local build that
  // outran prod), the user already has fresh code — toast would be noise.
  // Silently skip if either string isn't valid semver.
  if (!semver.valid(serverFeVersion) || !semver.valid(FE_BUILD_VERSION)) return;
  if (!semver.gt(serverFeVersion, FE_BUILD_VERSION)) return;

  mismatchNotified = true;

  // captureMessage (not just a breadcrumb) so each unique session shows up
  // as a Sentry event — lets ops measure how often users straddle deploys.
  // No-op when Sentry isn't initialized (REACT_APP_SENTRY_DSN === 'null').
  Sentry.captureMessage('FE/BE version mismatch detected', {
    level: 'warning',
    tags: {
      fe_build_version: FE_BUILD_VERSION,
      server_fe_version: serverFeVersion,
    },
  });

  window.dispatchEvent(
    new CustomEvent(VERSION_MISMATCH_EVENT, {
      detail: { expected: serverFeVersion, current: FE_BUILD_VERSION },
    }),
  );
};

export default checkVersionMismatch;
